import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================
// EXCEL EXPORT UTILITIES
// ============================================

/**
 * Export data to Excel with formatting
 */
export const exportToExcel = (data, config) => {
  const {
    fileName = 'export',
    sheetName = 'Data',
    headers = [],
    columnWidths = [],
    title = '',
    subtitle = '',
    summaryStats = null
  } = config;

  // Create workbook
  const wb = XLSX.utils.book_new();
  
  // Prepare data with headers
  const wsData = [];
  
  // Add title and subtitle if provided
  if (title) {
    wsData.push([title]);
    wsData.push([subtitle || `Generated on ${new Date().toLocaleString()}`]);
    wsData.push([]); // Empty row
  }
  
  // Add summary statistics if provided
  if (summaryStats) {
    Object.entries(summaryStats).forEach(([key, value]) => {
      wsData.push([key, value]);
    });
    wsData.push([]); // Empty row
  }
  
  // Add headers
  if (headers.length > 0) {
    wsData.push(headers);
  }
  
  // Add data rows
  data.forEach(row => {
    if (Array.isArray(row)) {
      wsData.push(row);
    } else {
      // Convert object to array based on headers order
      const rowData = headers.map(h => {
        const key = h.toLowerCase().replace(/\s+/g, '_');
        return row[key] ?? row[h] ?? '';
      });
      wsData.push(rowData);
    }
  });
  
  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  
  // Set column widths
  if (columnWidths.length > 0) {
    ws['!cols'] = columnWidths.map(w => ({ wch: w }));
  }
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  
  // Generate buffer
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  
  // Save file
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  saveAs(blob, `${fileName}_${formatDateForFile(new Date())}.xlsx`);
};

/**
 * Export multiple sheets to a single Excel file
 */
export const exportMultipleSheetsToExcel = (sheetsData, fileName = 'export') => {
  const wb = XLSX.utils.book_new();
  
  sheetsData.forEach(({ data, sheetName, headers, title }) => {
    const wsData = [];
    
    if (title) {
      wsData.push([title]);
      wsData.push([`Generated on ${new Date().toLocaleString()}`]);
      wsData.push([]);
    }
    
    if (headers?.length > 0) {
      wsData.push(headers);
    }
    
    data.forEach(row => {
      if (Array.isArray(row)) {
        wsData.push(row);
      }
    });
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31)); // Excel sheet name limit
  });
  
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  saveAs(blob, `${fileName}_${formatDateForFile(new Date())}.xlsx`);
};

// ============================================
// COSTING ERRORS EXPORT
// ============================================

export const prepareCostingDataForExport = (errors, filters = {}) => {
  const headers = ['Tracking', 'Client', 'Product', 'Errors', 'Date', 'Weight (g)', 'Qty', 'Cost'];
  
  const rows = errors.map(e => [
    e.tracking || '',
    e.client_code || '',
    e.second_name || e.product || '',
    (e.errors || []).join('; '),
    e.order_date || '',
    e.gross_weight || e.parcel_gross_weight || '',
    e.qty || '',
    e.medicine_cost || ''
  ]);
  
  const summaryStats = {
    'Total Records': errors.length,
    'Filter - Client': filters.client || 'All',
    'Filter - Error Type': filters.errorType || 'All',
    'Filter - Date Range': filters.dateFrom && filters.dateTo 
      ? `${filters.dateFrom} to ${filters.dateTo}` 
      : 'All'
  };
  
  return { headers, rows, summaryStats };
};

// ============================================
// SALES ERRORS EXPORT
// ============================================

export const prepareSalesDataForExport = (sales, filters = {}, hideFinancials = false) => {
  // Adjust headers based on role (hide financial columns for error_manager)
  const headers = hideFinancials 
    ? ['Tracking', 'Client', 'Customer Name', 'Cost Price (₹)', 'Error Type', 'Date']
    : ['Tracking', 'Client', 'Customer Name', 'Sell Price (₹)', 'Cost Price (₹)', 'Profit (₹)', 'Error Type', 'Date'];
  
  const rows = sales.map(s => {
    const baseRow = [
      s.tracking || s.tracking_number || '',
      s.client_code || '',
      s.customer_name || s.name || ''
    ];
    
    if (!hideFinancials) {
      baseRow.push(s.sell_price || 0);
    }
    
    baseRow.push(s.cost_price || 'Missing');
    
    if (!hideFinancials) {
      baseRow.push(s.cost_price ? (s.sell_price - s.cost_price) : '-');
    }
    
    baseRow.push(s.error_type || 'Missing Cost');
    baseRow.push(s.sale_date || '');
    
    return baseRow;
  });
  
  const totalSellPrice = sales.reduce((sum, s) => sum + (s.sell_price || 0), 0);
  const totalCostPrice = sales.reduce((sum, s) => sum + (s.cost_price || 0), 0);
  
  // Adjust summary stats based on role
  const summaryStats = hideFinancials ? [
    { label: 'Total Records', value: sales.length },
    { label: 'Filter - Client', value: filters.client || 'All' },
    { label: 'Filter - Date Range', value: filters.dateFrom && filters.dateTo 
      ? `${filters.dateFrom} to ${filters.dateTo}` 
      : 'All' }
  ] : [
    { label: 'Total Records', value: sales.length },
    { label: 'Total Sell Value', value: `₹${totalSellPrice.toLocaleString()}` },
    { label: 'Total Cost Value', value: `₹${totalCostPrice.toLocaleString()}` },
    { label: 'Filter - Client', value: filters.client || 'All' },
    { label: 'Filter - Date Range', value: filters.dateFrom && filters.dateTo 
      ? `${filters.dateFrom} to ${filters.dateTo}` 
      : 'All' }
  ];
  
  return { headers, rows, summaryStats };
};

// ============================================
// UNASSIGNED ORDERS EXPORT
// ============================================

export const prepareUnassignedDataForExport = (orders, filters = {}) => {
  const headers = ['Tracking', 'Recipient', 'Country', 'Total Cost (₹)', 'Date'];
  
  const rows = orders.map(o => [
    o.tracking || '',
    o.recipient_name || '',
    o.country || '',
    o.total_cost || 0,
    o.order_date || ''
  ]);
  
  const totalCost = orders.reduce((sum, o) => sum + (o.total_cost || 0), 0);
  
  const summaryStats = {
    'Total Unassigned Orders': orders.length,
    'Total Value': `₹${totalCost.toLocaleString()}`,
    'Filter - Date Range': filters.dateFrom && filters.dateTo 
      ? `${filters.dateFrom} to ${filters.dateTo}` 
      : 'All'
  };
  
  return { headers, rows, summaryStats };
};

// ============================================
// PDF EXPORT UTILITIES
// ============================================

/**
 * Generate PDF report for resolution history with detailed narrations
 */
export const generateHistoryPDF = (historyData, reportType = 'today') => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Filter data based on report type
  const now = new Date();
  const filteredData = historyData.filter(item => {
    const itemDate = new Date(item.timestamp);
    switch (reportType) {
      case 'today':
        return itemDate.toDateString() === now.toDateString();
      case 'weekly':
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return itemDate >= weekAgo;
      case 'monthly':
        const monthAgo = new Date(now);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return itemDate >= monthAgo;
      default:
        return true;
    }
  });
  
  // Header
  doc.setFillColor(59, 130, 246); // Blue
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('EAC', 14, 18);
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('Error Resolution Report', 14, 28);
  
  doc.setFontSize(10);
  const reportTypeLabel = reportType === 'today' ? "Today's Report" 
    : reportType === 'weekly' ? 'Weekly Report (Last 7 Days)' 
    : 'Monthly Report (Last 30 Days)';
  doc.text(reportTypeLabel, 14, 36);
  
  // Date on right
  doc.setFontSize(10);
  doc.text(`Generated: ${now.toLocaleString()}`, pageWidth - 14, 28, { align: 'right' });
  
  // Summary Section
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', 14, 55);
  
  // Calculate summary stats
  const totalResolutions = filteredData.length;
  const uniqueUsers = [...new Set(filteredData.map(h => h.username))].length;
  const resolutionTypes = {};
  filteredData.forEach(h => {
    const type = h.data_accessed?.resolution_type || h.action || 'other';
    resolutionTypes[type] = (resolutionTypes[type] || 0) + 1;
  });
  
  // Summary boxes
  doc.setFillColor(240, 249, 255);
  doc.roundedRect(14, 60, 55, 25, 3, 3, 'F');
  doc.roundedRect(74, 60, 55, 25, 3, 3, 'F');
  doc.roundedRect(134, 60, 60, 25, 3, 3, 'F');
  
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(59, 130, 246);
  doc.text(String(totalResolutions), 42, 74, { align: 'center' });
  doc.text(String(uniqueUsers), 102, 74, { align: 'center' });
  doc.text(Object.keys(resolutionTypes).length.toString(), 164, 74, { align: 'center' });
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Total Resolutions', 42, 81, { align: 'center' });
  doc.text('Active Users', 102, 81, { align: 'center' });
  doc.text('Resolution Types', 164, 81, { align: 'center' });
  
  // Resolution Type Breakdown
  let yPos = 95;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Resolution Types Breakdown:', 14, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  Object.entries(resolutionTypes).forEach(([type, count]) => {
    const formattedType = type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    doc.text(`• ${formattedType}: ${count}`, 20, yPos);
    yPos += 6;
  });
  
  yPos += 10;
  
  // Detailed Table with narrations and full details
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Resolution Details', 14, yPos);
  yPos += 5;
  
  const tableData = filteredData.map(h => {
    // Build detailed info including narrations
    const details = [];
    
    // Check for narration
    if (h.data_accessed?.narration_text) {
      details.push(`Narration: ${h.data_accessed.narration_text}`);
    }
    
    // Product resolution details
    const products = h.data_accessed?.products_resolved || [];
    if (products.length > 0) {
      details.push(products.map(p => `${p.product} → ${p.matched_to}`).join(', '));
    }
    
    // Other resolution info
    if (h.data_accessed?.resolution?.found_product) {
      details.push(`Product: ${h.data_accessed.resolution.found_product}`);
    }
    if (h.data_accessed?.cost_assigned) {
      details.push(`Cost: ₹${h.data_accessed.cost_assigned.toLocaleString()}`);
    }
    if (h.data_accessed?.linked_to) {
      details.push(`Linked: ${h.data_accessed.linked_to}`);
    }
    if (h.data_accessed?.notes) {
      details.push(`Notes: ${h.data_accessed.notes}`);
    }
    if (h.data_accessed?.ignore_reason) {
      details.push(`Reason: ${h.data_accessed.ignore_reason}`);
    }
    
    // Fallback to description
    if (details.length === 0 && h.description) {
      details.push(h.description);
    }
    
    const detailText = details.join(' | ');
    
    return [
      new Date(h.timestamp).toLocaleString(),
      `${h.username || '-'}${h.role ? ` (${h.role})` : ''}`,
      h.data_accessed?.tracking || '-',
      (h.data_accessed?.resolution_type || h.action || 'other').replace(/_/g, ' '),
      detailText.length > 60 ? detailText.substring(0, 57) + '...' : detailText
    ];
  });
  
  autoTable(doc, {
    startY: yPos,
    head: [['Date/Time', 'User', 'Tracking', 'Action Type', 'Details / Narration']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [59, 130, 246],
      textColor: 255,
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 7,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 32 },
      1: { cellWidth: 28 },
      2: { cellWidth: 32 },
      3: { cellWidth: 25 },
      4: { cellWidth: 'auto' }
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      // Footer on each page
      const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `EAC Error Resolution Report | Page ${pageNum} | Generated: ${now.toLocaleDateString()}`,
        pageWidth / 2, 
        doc.internal.pageSize.getHeight() - 10, 
        { align: 'center' }
      );
    }
  });
  
  // Save PDF
  const dateStr = now.toISOString().split('T')[0];
  doc.save(`EAC_Resolution_Report_${reportType}_${dateStr}.pdf`);
};

// ============================================
// PRINT UTILITIES
// ============================================

/**
 * Generate printable HTML content
 */
export const generatePrintableHTML = (config) => {
  const {
    title,
    subtitle,
    summaryStats = {},
    tables = [], // Array of { title, headers, rows }
    filters = {}
  } = config;
  
  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 20px;
          font-size: 12px;
        }
        .header {
          border-bottom: 3px solid #3b82f6;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        .header h1 { color: #1e40af; font-size: 24px; margin-bottom: 5px; }
        .header p { color: #64748b; font-size: 14px; }
        .header .date { float: right; color: #94a3b8; font-size: 11px; }
        .summary-box {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .stat-card {
          background: #f0f9ff;
          padding: 12px 20px;
          border-radius: 8px;
          border-left: 4px solid #3b82f6;
        }
        .stat-card .label { color: #64748b; font-size: 11px; }
        .stat-card .value { color: #1e40af; font-size: 18px; font-weight: bold; }
        .filters {
          background: #fef3c7;
          padding: 10px 15px;
          border-radius: 6px;
          margin-bottom: 20px;
          font-size: 11px;
        }
        .filters span { margin-right: 20px; }
        .filters strong { color: #92400e; }
        .table-section { margin-bottom: 30px; }
        .table-section h3 { 
          color: #334155; 
          font-size: 14px; 
          margin-bottom: 10px;
          padding-bottom: 5px;
          border-bottom: 1px solid #e2e8f0;
        }
        table { 
          width: 100%; 
          border-collapse: collapse; 
          font-size: 11px;
        }
        th { 
          background: #3b82f6; 
          color: white; 
          padding: 8px 10px; 
          text-align: left;
          font-weight: 600;
        }
        td { 
          padding: 6px 10px; 
          border-bottom: 1px solid #e2e8f0;
        }
        tr:nth-child(even) { background: #f8fafc; }
        tr:hover { background: #f0f9ff; }
        .error-badge {
          background: #fee2e2;
          color: #991b1b;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
        }
        .footer {
          margin-top: 30px;
          padding-top: 15px;
          border-top: 1px solid #e2e8f0;
          text-align: center;
          color: #94a3b8;
          font-size: 10px;
        }
        @media print {
          body { padding: 10px; }
          .no-print { display: none; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <span class="date">${new Date().toLocaleString()}</span>
        <h1>EAC - ${title}</h1>
        <p>${subtitle || ''}</p>
      </div>
  `;
  
  // Summary stats
  if (Object.keys(summaryStats).length > 0) {
    html += '<div class="summary-box">';
    Object.entries(summaryStats).forEach(([label, value]) => {
      html += `
        <div class="stat-card">
          <div class="label">${label}</div>
          <div class="value">${value}</div>
        </div>
      `;
    });
    html += '</div>';
  }
  
  // Filters
  if (Object.keys(filters).length > 0) {
    html += '<div class="filters"><strong>Filters Applied:</strong> ';
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        html += `<span>${key}: <strong>${value}</strong></span>`;
      }
    });
    html += '</div>';
  }
  
  // Tables
  tables.forEach(table => {
    html += `
      <div class="table-section">
        <h3>${table.title} (${table.rows.length} records)</h3>
        <table>
          <thead>
            <tr>${table.headers.map(h => `<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${table.rows.map(row => `
              <tr>${row.map(cell => `<td>${cell || '-'}</td>`).join('')}</tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  });
  
  html += `
      <div class="footer">
        EAC Export | Generated on ${new Date().toLocaleString()} | Total Records: ${tables.reduce((sum, t) => sum + t.rows.length, 0)}
      </div>
    </body>
    </html>
  `;
  
  return html;
};

/**
 * Open print preview with generated HTML
 */
export const openPrintPreview = (config) => {
  const html = generatePrintableHTML(config);
  
  // Open a preview window that shows content first
  const previewWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes');
  
  if (!previewWindow) {
    alert('Please allow popups to view print preview');
    return;
  }
  
  previewWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print Preview - ${config.title || 'Document'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #f1f5f9;
            min-height: 100vh;
          }
          
          /* Toolbar */
          .preview-toolbar {
            position: sticky;
            top: 0;
            z-index: 100;
            background: linear-gradient(to right, #1e40af, #3b82f6);
            padding: 12px 24px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          .preview-toolbar h1 {
            color: white;
            font-size: 18px;
            font-weight: 600;
          }
          .preview-toolbar .subtitle {
            color: rgba(255,255,255,0.8);
            font-size: 12px;
            margin-top: 2px;
          }
          .toolbar-buttons {
            display: flex;
            gap: 10px;
          }
          .toolbar-btn {
            padding: 10px 20px;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.2s;
          }
          .btn-print {
            background: white;
            color: #1e40af;
          }
          .btn-print:hover {
            background: #f0f9ff;
            transform: translateY(-1px);
          }
          .btn-pdf {
            background: #059669;
            color: white;
          }
          .btn-pdf:hover {
            background: #047857;
            transform: translateY(-1px);
          }
          .btn-close {
            background: rgba(255,255,255,0.2);
            color: white;
          }
          .btn-close:hover {
            background: rgba(255,255,255,0.3);
          }
          
          /* Preview container */
          .preview-container {
            padding: 24px;
            display: flex;
            justify-content: center;
          }
          .preview-page {
            background: white;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            border-radius: 8px;
            max-width: 900px;
            width: 100%;
            overflow: hidden;
          }
          .preview-content {
            padding: 40px;
          }
          
          /* Print tip */
          .print-tip {
            background: #fef3c7;
            border: 1px solid #f59e0b;
            color: #92400e;
            padding: 10px 16px;
            margin: 16px 24px;
            border-radius: 8px;
            font-size: 13px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          /* Hide toolbar and tip when printing */
          @media print {
            .preview-toolbar, .print-tip { display: none !important; }
            .preview-container { padding: 0; }
            .preview-page { box-shadow: none; border-radius: 0; }
            body { background: white; }
          }
        </style>
        <script>
          // Print function that keeps window open after print/cancel
          function handlePrint() {
            window.print();
            // Window stays open - user must manually close it
          }
        </script>
      </head>
      <body>
        <div class="preview-toolbar">
          <div>
            <h1>🖨️ Print Preview</h1>
            <div class="subtitle">${config.title || 'Document'}</div>
          </div>
          <div class="toolbar-buttons">
            <button class="toolbar-btn btn-close" onclick="window.close()">
              ✕ Close
            </button>
            <button class="toolbar-btn btn-pdf" onclick="handlePrint()">
              📄 Save as PDF
            </button>
            <button class="toolbar-btn btn-print" onclick="handlePrint()">
              🖨️ Print
            </button>
          </div>
        </div>
        
        <div class="print-tip">
          💡 <strong>Tip:</strong> To save as PDF, click "Print" and select "Save as PDF" in your printer options. The preview will stay open after printing.
        </div>
        
        <div class="preview-container">
          <div class="preview-page">
            <div class="preview-content">
              ${html}
            </div>
          </div>
        </div>
      </body>
    </html>
  `);
  
  previewWindow.document.close();
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatDateForFile = (date) => {
  return date.toISOString().split('T')[0].replace(/-/g, '');
};

export const getDateRangeLabel = (type) => {
  const now = new Date();
  switch (type) {
    case 'today':
      return now.toLocaleDateString();
    case 'weekly':
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return `${weekAgo.toLocaleDateString()} - ${now.toLocaleDateString()}`;
    case 'monthly':
      const monthAgo = new Date(now);
      monthAgo.setDate(monthAgo.getDate() - 30);
      return `${monthAgo.toLocaleDateString()} - ${now.toLocaleDateString()}`;
    default:
      return 'All Time';
  }
};
